/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import hammerheadLogo from '/images/hammerhead-logo.svg';
import quarqLogo from '/images/quarq-logo.svg';
import rockshoxLogo from '/images/rockshox-logo.svg';
// The SRAM mark is the SHARED Figma wordmark (`SRAM/Wordmark`, node 400:2160) that the
// header and footer already use — not a scraped copy. brand-assets/spec.md requires a
// single wordmark asset, so `sram-logo.svg` was removed rather than kept as a duplicate.
import sramLogo from '/images/logo.svg';
import timeLogo from '/images/time-logo.svg';
import truvativLogo from '/images/truvativ-logo.svg';
import velocioLogo from '/images/velocio-logo.svg';
import zippLogo from '/images/zipp-logo.svg';

/**
 * SRAM family brand assets, split out of `index.tsx` because
 * `react-refresh/only-export-components` (error-level under `--max-warnings 0`) rejects a component
 * file that also exports constants. Same split as `ui/segmented-button-styles.ts`.
 *
 * ES imports rather than bare `/images/...` strings so the URL resolves through Vite's configured
 * `base` and a renamed asset fails the build instead of 404-ing at runtime. (Not for hashing —
 * `public/` bypasses the asset pipeline.)
 */
export interface BrandLogoAsset {
    src: string;
    /** English brand name — the `defaultValue` when a locale omits the key. */
    name: string;
    /**
     * Any width/height pair carrying the mark's true aspect ratio — only the RATIO is read, to
     * reserve space before the asset loads. The seven scraped marks use their viewBox verbatim;
     * `sram` uses its rendered `187x24`, which is the same 7.79 ratio as `logo.svg`'s `779x100`
     * viewBox and matches Figma `400:2160`.
     */
    width: number;
    height: number;
    /**
     * Recolouring for marks that are not already dark — only the shared SRAM wordmark, which is
     * white because it is authored for the red header chip. NOT `--header-logo-filter`: that token
     * is header-scoped and resolves to `none`, so reusing it would apply the wrong value.
     */
    filterClass?: string;
}

export const BRAND_LOGOS = {
    sram: { src: sramLogo, name: 'SRAM', width: 187, height: 24, filterClass: '[filter:brightness(0)]' },
    rockshox: { src: rockshoxLogo, name: 'RockShox', width: 138.09, height: 34.25 },
    truvativ: { src: truvativLogo, name: 'Truvativ', width: 64, height: 8.3 },
    zipp: { src: zippLogo, name: 'Zipp', width: 1245.3, height: 161.49 },
    quarq: { src: quarqLogo, name: 'Quarq', width: 59.3, height: 10 },
    time: { src: timeLogo, name: 'TIME Sport', width: 394.66, height: 47.13 },
    hammerhead: { src: hammerheadLogo, name: 'Hammerhead', width: 2067.88, height: 176.43 },
    velocio: { src: velocioLogo, name: 'Velocio', width: 80, height: 21 },
} as const satisfies Record<string, BrandLogoAsset>;

export type BrandKey = keyof typeof BRAND_LOGOS;

export const BRAND_KEYS = Object.keys(BRAND_LOGOS) as BrandKey[];

/**
 * Casing/punctuation-insensitive form: `'TIME Sport'` and `'time-sport'` both fold to `timesport`.
 *
 * Diacritics are FOLDED, not stripped — NFD then dropping combining marks turns `Vélocio` into
 * `velocio`, not `vlocio`.
 */
function normalizeBrand(raw: string): string {
    return raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

/** Derived from `BRAND_LOGOS`, so adding a brand stays a one-line map edit. */
const BRAND_ALIASES: Record<string, BrandKey> = Object.fromEntries(
    BRAND_KEYS.flatMap((key): [string, BrandKey][] => {
        const fromName = normalizeBrand(BRAND_LOGOS[key].name);
        // Never register an empty alias: a non-Latin display name normalises to '', and an
        // '' => brand entry would resolve every non-Latin catalogue brand to it.
        return fromName && fromName !== key
            ? [
                  [key, key],
                  [fromName, key],
              ]
            : [[key, key]];
    })
);

/**
 * Maps the free-text catalogue brand (SCAPI `Product.brand`) onto a {@link BrandKey}, or `null` when
 * it is not one of the eight family brands.
 *
 * Whole-string after normalisation, not a substring or prefix test — `Timex` must not resolve to
 * TIME. Callers treat `null` as "render the brand as plain text", not "no brand".
 */
export function resolveBrandKey(raw: string | undefined | null): BrandKey | null {
    if (!raw) return null;
    return BRAND_ALIASES[normalizeBrand(raw)] ?? null;
}
